<template>
  <b-dropdown
    right
    no-caret
    variant="link"
    toggle-class="notification-bell-toggle"
    menu-class="notification-bell-menu"
    @show="onOpen"
  >
    <template #button-content>
      <div class="notification-bell" :class="{ 'is-active': unread > 0 }">
        <b-icon icon="bell-fill" class="notification-bell-icon" />
        <span v-if="unread > 0" class="notification-bell-badge">
          {{ unread > 99 ? '99+' : unread }}
        </span>
      </div>
    </template>

    <div class="notification-bell-header">
      <span class="notification-bell-title">Уведомления</span>
      <button
        v-if="unread > 0"
        type="button"
        class="notification-bell-mark-all"
        @click="markAll"
      >Прочитать все</button>
    </div>

    <div v-if="!items.length" class="notification-bell-empty">
      Уведомлений нет
    </div>

    <div v-else class="notification-bell-list">
      <a
        v-for="n in items.slice(0, 8)"
        :key="n.id"
        href="javascript:void(0)"
        class="notification-bell-item"
        :class="{ 'is-unread': !n.isRead }"
        @click="openItem(n)"
      >
        <span class="notification-bell-dot" :style="{ background: dotColor(n.type) }"></span>
        <div class="notification-bell-body">
          <div class="notification-bell-row">
            <span class="notification-bell-item-title">{{ n.title }}</span>
            <span class="notification-bell-item-time">{{ formatTime(n.createdAt) }}</span>
          </div>
          <div v-if="n.body" class="notification-bell-item-body">{{ n.body }}</div>
        </div>
      </a>
    </div>

    <a
      href="javascript:void(0)"
      class="notification-bell-footer"
      @click="goToAll"
    >
      Все уведомления →
    </a>
  </b-dropdown>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'NotificationBell',
  computed: {
    ...mapState({
      items: 'notifications',
      unread: 'notificationsUnread',
    }),
  },
  methods: {
    onOpen() {
      // Re-pull on open so a slightly stale poll (we run every 30s)
      // doesn't show a misleading badge.
      this.$store.dispatch('fetchNotifications')
    },
    async openItem(n) {
      await this.$store.dispatch('markNotificationRead', n.id)
      if (n.link) this.$router.push(n.link).catch(() => {})
    },
    async markAll() {
      await this.$store.dispatch('markAllNotificationsRead')
    },
    goToAll() {
      this.$router.push('/notifications').catch(() => {})
    },
    dotColor(type) {
      // Type → colour token. Free-form string on the backend so new
      // types can be added without coordinating a release here.
      const map = {
        CLASSIFICATION_APPROVED: '#4ade80',
        CLASSIFICATION_REJECTED: '#EF4444',
      }
      return map[type] || '#EFB914'
    },
    formatTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const now = new Date()
      const sameDay = d.toDateString() === now.toDateString()
      if (sameDay) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
    },
  },
}
</script>

<style scoped>
.notification-bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: #f8fafc;
  transition: background .15s ease, color .15s ease, box-shadow .2s ease;
}
.notification-bell:hover { background: rgba(255, 255, 255, 0.12); }
.notification-bell-icon { font-size: 18px; transition: color .15s ease; }
/* Active = there are unread items. Light the icon up in the Payton
   accent yellow plus a soft glow so the bell reads as "look here"
   even before you spot the red counter badge. */
.notification-bell.is-active {
  background: rgba(239, 185, 20, 0.18);
  box-shadow: 0 0 0 2px rgba(239, 185, 20, 0.35), 0 0 18px rgba(239, 185, 20, 0.45);
  animation: notification-bell-pulse 2.2s ease-in-out infinite;
}
.notification-bell.is-active .notification-bell-icon { color: #EFB914; }
@keyframes notification-bell-pulse {
  0%, 100% { box-shadow: 0 0 0 2px rgba(239, 185, 20, 0.30), 0 0 14px rgba(239, 185, 20, 0.35); }
  50%      { box-shadow: 0 0 0 2px rgba(239, 185, 20, 0.50), 0 0 22px rgba(239, 185, 20, 0.60); }
}
.notification-bell-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #EF4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* Dropdown shell — neutral surface so it blends with the dark navbar */
::v-deep .notification-bell-menu {
  min-width: 360px;
  max-width: 380px;
  padding: 0;
  background: #1f2127;
  color: #f8fafc;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
}
::v-deep .notification-bell-toggle {
  background: transparent !important;
  border: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
}
::v-deep .notification-bell-toggle:focus { box-shadow: none !important; }

.notification-bell-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.notification-bell-title {
  font-size: 13px;
  font-weight: 700;
  color: #f8fafc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.notification-bell-mark-all {
  background: transparent;
  border: 0;
  color: #EFB914;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
}
.notification-bell-mark-all:hover { background: rgba(239, 185, 20, 0.1); }

.notification-bell-empty {
  padding: 24px 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
}
.notification-bell-list { max-height: 380px; overflow-y: auto; }
.notification-bell-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  text-decoration: none;
  color: #f8fafc;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background .12s ease;
}
.notification-bell-item:hover { background: rgba(255, 255, 255, 0.04); text-decoration: none; }
.notification-bell-item.is-unread { background: rgba(239, 185, 20, 0.04); }
.notification-bell-dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  margin-top: 7px;
}
.notification-bell-body { flex: 1; min-width: 0; }
.notification-bell-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  justify-content: space-between;
}
.notification-bell-item-title {
  font-size: 13px;
  font-weight: 600;
  color: #f8fafc;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.notification-bell-item-time {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  flex: 0 0 auto;
}
.notification-bell-item-body {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.notification-bell-footer {
  display: block;
  text-align: center;
  padding: 12px 16px;
  font-size: 13px;
  color: #EFB914;
  text-decoration: none;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
}
.notification-bell-footer:hover {
  background: rgba(239, 185, 20, 0.08);
  text-decoration: none;
}
</style>
